import { Tab } from "@mui/base/Tab";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "react-loading-skeleton/dist/skeleton.css";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { Site, SiteCreateAttributes, SiteUpdateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { useTeam } from "~/contexts/TeamProvider";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatchParsed } from "~/helpers/tryCatch";
import {
  MappedValidationError,
  VALIDATION_ERRORS,
  mapValidationErrors,
} from "~/utils/errors/validationErrors";

import { getRegionsAndLanguages, uploadFile } from "../../../api/apiFunctions";
import styles from "./NewSite.module.scss";
import NewSiteCompanyDetails from "./NewSiteCompanyDetails";
import NewSiteDetails from "./NewSiteDetails";
import NewSiteKeywords from "./NewSiteKeywords";
import NewSiteVisibilityEngineHandoff from "./NewSiteVisibilityEngineHandoff";
import NewSiteVisibilitySettings from "./NewSiteVisibilitySettings";
import NewSiteWordpressInstall from "./NewSiteWordpressInstall";
import NewSiteAutomations from "./newSiteAutomations/NewSiteAutomations";
import PendingLoader from "./pendingLoader/PendingLoader";

export const tabs = {
  1: "/sites/wizard/new",
  2: "/sites/wizard/details",
  3: "/sites/wizard/keywords",
  4: "/sites/wizard/automations",
  5: "/sites/wizard/settings",
  integration: "/sites/wizard/integration",
};

function useRouteMatch(patterns: string[]) {
  const { pathname } = useLocation();
  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

type Props = {
  setWarningText: (text: string | null) => void;
  setSiteName: (name: string | null) => void;
};

const NewSite = ({ setWarningText, setSiteName }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { team } = useTeam();
  const siteId = localStorage.getItem("siteId");
  const form = useForm<SiteCreateAttributes>({
    defaultValues: { platform_cms: "wordpress" },
  });
  const { setValue } = form;

  const [activeTab, setActiveTab] = useState<string>();
  const [serverErrors, setServerErrors] = useState<MappedValidationError[]>([]);
  const [newSiteId, setNewSiteId] = useState<number>(Number(siteId));
  const [site, setSite] = useState<Site | undefined | null>(undefined);

  // The suggestions poll below only feeds the Keywords step (step 3), so scope
  // it to that route — no need to poll the site while on other wizard steps.
  const { pathname } = useLocation();
  const isKeywordsStep = !!matchPath(tabs[3], pathname);

  const { data: companyDetails, isFetching: isFetchingCompanyDetails } =
    useQuery({
      queryKey: ["teamList"],
      queryFn: async () => {
        const responce = await getRegionsAndLanguages();

        return {
          regions: responce.regions,
          languages: responce.languages,
          pageUsageMax: responce.pageUsageMax,
        };
      },
    });
  useQuery({
    queryKey: ["sitesDetail"],
    queryFn: async () => {
      const response = await apiClient.sites.sitesDetail(newSiteId);
      const siteResponse = response?.data?.site;
      setSite(siteResponse);
      setValue("url", siteResponse.url);
      setValue(
        "page_max_override",
        siteResponse?.page_max_override || undefined,
      );
      if (siteResponse.crawl_mode === "normal") {
        // @ts-expect-error BE expect string but it is boolean
        setValue("crawl_mode", false);
      }
      if (siteResponse.crawl_mode === "whitelisted") {
        // @ts-expect-error BE expect string but it is boolean
        setValue("crawl_mode", true);
      }

      return response;
    },
    enabled: !!newSiteId && !!companyDetails,
  });

  // Intentionally kept as a separate query from the "sitesDetail" one above,
  // even though both hit sitesDetail(newSiteId) (one extra request on step 3).
  // They serve different roles: the query above seeds the form via setValue on
  // every fetch, while this one only polls for suggestions and sets `site`.
  // Merging them would run the form-seeding setValue on every poll and wipe the
  // user's edits, so the duplicate is deliberate and left as-is.
  useQuery({
    queryKey: ["sitesDetailSuggestions"],
    queryFn: async () => {
      const response = await apiClient.sites.sitesDetail(newSiteId);
      const siteResponse = response?.data?.site;
      setSite(siteResponse);

      return response;
    },
    enabled:
      isKeywordsStep &&
      (newSiteId ? site?.suggestions_status !== "completed" : !!newSiteId),
    refetchInterval(query) {
      if (site?.suggestions_status === "completed") {
        return false;
      }
      if (query?.state?.data?.data?.site?.suggestions_status === "completed") {
        return false;
      }

      return 3000;
    },
  });

  const isSuggestionsKeywordsCompleted =
    site?.suggestions_status === "completed";

  const { data: widgetDetail } = useQuery({
    queryKey: ["widgetDetail"],
    queryFn: async () => {
      return await apiClient.sites.widgetDetail(newSiteId);
    },
    enabled: !!newSiteId,
  });

  const { mutate: updateSite, isPending: isPendingUpdateSite } = useMutation({
    mutationFn: async (data: SiteUpdateAttributes) => {
      return await apiClient.sites.sitesPartialUpdate(newSiteId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sitesDetail"] });
      enqueueSnackbar(`${site?.url} was updated successfully!`, {
        variant: "success",
      });
      setActiveTab(tabs[3]);
      navigate(tabs[3]);
    },
    onError: (error) => {
      //@ts-expect-error BE Type rethink
      enqueueSnackbar(error?.error?.error, {
        variant: "error",
        persist: true,
      });
    },
  });
  const { mutate: mutateFile, isPending: isPendingFile } = useMutation({
    mutationFn: ({
      file,
      collection,
      reflection,
    }: {
      file: File;
      collection?: string;
      reflection?: string;
    }) => uploadFile(file, collection, reflection),
    onError: () => {
      enqueueSnackbar("File is not uploaded", {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: async (response, { file }) => {
      enqueueSnackbar("File uploaded successfully!", { variant: "success" });
      const data =
        typeof response.json === "function" ? await response.json() : response;
      const { direct_upload } = data;
      if (!direct_upload?.url) return;

      try {
        await fetch(direct_upload.url, {
          method: "PUT",
          headers: direct_upload.headers,
          body: file,
        });
        updateSite({
          page_imports_attributes: [{ input_file: data.signed_id }],
        });
        enqueueSnackbar("File uploaded successfully!", {
          variant: "success",
        });
      } catch {
        enqueueSnackbar("Error uploading file", {
          variant: "error",
          persist: true,
        });
      }
    },
  });

  const { mutate: createSite, isPending: isPendingCreateSite } = useMutation({
    mutationFn: async (data: SiteCreateAttributes) => {
      const result = await tryCatchParsed(apiClient.sites.sitesCreate(data));

      if (result.error) {
        const errors = mapValidationErrors(result.error);

        if (errors) {
          setServerErrors(errors);

          const quotaError = errors.find(
            (e) => e.code === VALIDATION_ERRORS.SITE_QUOTA_EXCEEDED,
          );

          if (quotaError) {
            setWarningText(quotaError.message);
          } else {
            enqueueSnackbar(messageFromError(result.error), {
              variant: "error",
              persist: true,
            });
          }
        }

        throw result.error;
      }

      setWarningText(null);
      const response = result.data;
      const siteResponse = response.data.site;

      setNewSiteId(siteResponse.id);
      localStorage.setItem("siteId", siteResponse.id.toString());

      return response;
    },

    onSuccess: (response) => {
      const site = response?.data?.site;
      setSite(site);

      enqueueSnackbar(`${site?.fqdn} was added successfully!`, {
        variant: "success",
      });

      const primaryLanguage = site?.primary_language
        ? companyDetails?.languages?.[site.primary_language]
        : undefined;

      const primaryCountry = site?.primary_country
        ? `${site.primary_country} (${companyDetails?.regions?.[site.primary_country]?.name})`
        : undefined;

      setValue("primary_country", primaryCountry);
      setValue("primary_language", primaryLanguage);

      setServerErrors([]);
      setActiveTab(tabs[2]);
      navigate(tabs[2]);
    },
  });

  const isDisabled = true;
  const isPendingSite = isPendingCreateSite || isPendingFile;

  useEffect(() => {
    if (isSuggestionsKeywordsCompleted) {
      queryClient.invalidateQueries({ queryKey: ["keywordSuggestions"] });
    }
  }, [isSuggestionsKeywordsCompleted]);

  useEffect(() => {
    setSiteName(site?.fqdn ?? null);
  }, [site?.fqdn, setSiteName]);

  const routeMatch = useRouteMatch(Object.values(tabs));

  const currentTab = routeMatch?.pattern?.path;
  // Only plans entitled to the AI Visibility Engine (prerendering) get the
  // altered wizard flow; non-entitled plans stay on the legacy onboarding.
  const hasVisibilityEngineEntitlement = Boolean(
    team?.subscription?.price?.product?.prerendering,
  );
  const isWordpress =
    site?.platform_cms === "wordpress" && hasVisibilityEngineEntitlement;
  const showVisibilityEngineHandoff =
    hasVisibilityEngineEntitlement && site?.platform_cms !== "wordpress";
  const step4Path = showVisibilityEngineHandoff ? tabs.integration : tabs[4];
  const step4Title = isWordpress
    ? "Install"
    : showVisibilityEngineHandoff
      ? "Integration"
      : "Automations";

  return (
    <div>
      <div className={styles.card}>
        <Tabs
          defaultValue={activeTab}
          className={styles.tabs}
          value={currentTab}
        >
          <TabsList className={styles.tabsList}>
            <div className={styles.stepsDivider} />
            <Tab value={tabs[1]} className={styles.tab} disabled={isDisabled}>
              <span className={styles.stepNumber}>1</span>
              <span className={styles.stepTitle}>Set Domain</span>
            </Tab>
            <Tab value={tabs[2]} className={styles.tab} disabled={isDisabled}>
              <span className={styles.stepNumber}>2</span>
              <span className={styles.stepTitle}>Add Details</span>
            </Tab>
            <Tab value={tabs[3]} className={styles.tab} disabled={false}>
              <span className={styles.stepNumber}>3</span>
              <span className={styles.stepTitle}>Keywords</span>
            </Tab>
            {showVisibilityEngineHandoff ? (
              <>
                <Tab
                  value={tabs.integration}
                  className={styles.tab}
                  disabled={false}
                >
                  <span className={styles.stepNumber}>4</span>
                  <span className={styles.stepTitle}>Integration</span>
                </Tab>
                <Tab value={tabs[4]} className={styles.tab} disabled={false}>
                  <span className={styles.stepNumber}>5</span>
                  <span className={styles.stepTitle}>Snippet</span>
                </Tab>
              </>
            ) : (
              <Tab value={tabs[4]} className={styles.tab} disabled={false}>
                <span className={styles.stepNumber}>4</span>
                <span className={styles.stepTitle}>{step4Title}</span>
              </Tab>
            )}
            {isWordpress && (
              <Tab value={tabs[5]} className={styles.tab} disabled={false}>
                <span className={styles.stepNumber}>5</span>
                <span className={styles.stepTitle}>Settings</span>
              </Tab>
            )}
          </TabsList>
          <TabPanel value={tabs[1]}>
            <NewSiteDetails
              form={form}
              setActiveTab={setActiveTab}
              serverErrors={serverErrors}
              createSite={createSite}
              newSiteId={newSiteId}
              mutateFile={mutateFile}
              isPendingSite={isPendingSite}
              pageUsageMax={companyDetails?.pageUsageMax}
              hasVisibilityEngineEntitlement={hasVisibilityEngineEntitlement}
            />
          </TabPanel>
          <TabPanel value={tabs[2]}>
            <NewSiteCompanyDetails
              form={form}
              site={site}
              setActiveTab={setActiveTab}
              serverErrors={serverErrors}
              updateSite={updateSite}
              isPendingUpdateSite={isPendingUpdateSite}
              regions={companyDetails?.regions ?? {}}
              languages={companyDetails?.languages ?? {}}
              isFetchingCompanyDetails={isFetchingCompanyDetails}
            />
          </TabPanel>
          <TabPanel value={tabs[3]}>
            {isSuggestionsKeywordsCompleted ? (
              <NewSiteKeywords
                newSiteId={newSiteId}
                setActiveTab={setActiveTab}
                site={site}
                nextTab={step4Path}
              />
            ) : (
              <PendingLoader
                isPendingLoader={isSuggestionsKeywordsCompleted}
                onSkip={() => {
                  setActiveTab(step4Path);
                  navigate(step4Path);
                }}
              />
            )}
          </TabPanel>
          {showVisibilityEngineHandoff && (
            <TabPanel value={tabs.integration}>
              <NewSiteVisibilityEngineHandoff
                siteFqdn={site?.fqdn}
                setActiveTab={setActiveTab}
              />
            </TabPanel>
          )}
          <TabPanel value={tabs[4]}>
            {isWordpress ? (
              <NewSiteWordpressInstall
                site={site}
                setActiveTab={setActiveTab}
              />
            ) : (
              <NewSiteAutomations
                widgetDetail={widgetDetail?.data.site_widget}
                newSiteId={newSiteId}
                setActiveTab={setActiveTab}
                backTab={
                  showVisibilityEngineHandoff ? tabs.integration : tabs[3]
                }
              />
            )}
          </TabPanel>
          {isWordpress && (
            <TabPanel value={tabs[5]}>
              <NewSiteVisibilitySettings
                newSiteId={newSiteId}
                setActiveTab={setActiveTab}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
    </div>
  );
};
export default NewSite;
